import {useEffect, useState} from "react";

import {clearCache, withCache} from "../cache";
import {useApi} from "../PlayMakersProvider";
import {RewardType} from "../types";

type FetchRewardTypeMethod = (quiet?: boolean, refresh?: boolean) => Promise<RewardType>;
interface UseRewardTypeReturn {
  refresh: FetchRewardTypeMethod;
  rewardType: RewardType | null;
  fetched: boolean;
}

export const useRewardType = (id?: string): UseRewardTypeReturn => {
  const {getRewardType} = useApi();

  const [rewardType, setRewardType] = useState<RewardType | null>(null);
  const [fetched, setFetched] = useState<boolean>(false);

  const fetchRewardType: FetchRewardTypeMethod = async(quiet = false, refresh) => {
    if(!quiet) setFetched(false);
    if(refresh) clearCache("getRewardType", [id]);

    try {
      if(!id) throw new Error("No reward type to fetch");

      const fetchedRewardType = await withCache(getRewardType, [id], 120_000);
      setRewardType(fetchedRewardType);

      return fetchedRewardType;
    } catch (err) {
      console.error("Error fetching reward type", err);
      throw new Error("Error fetching reward type", {cause: err});
    } finally {
      setFetched(false);
    }
  };

  useEffect(() => {
    if(id) fetchRewardType();
  }, [id]);

  return {
    fetched,
    refresh: quiet => fetchRewardType(quiet, true),
    rewardType
  };
};

type FetchRewardTypesMethod = (quiet?: boolean, refresh?: boolean) => Promise<RewardType[]>;
interface UseRewardTypesReturn {
  refresh: FetchRewardTypesMethod;
  rewardTypes: RewardType[];
  fetched: boolean;
}

export const useRewardTypes = (params: {[key: string]: any} = {}): UseRewardTypesReturn => {
  const {getRewardTypes} = useApi();

  const [rewardTypes, setRewardTypes] = useState<RewardType[]>([]);
  const [fetched, setFetched] = useState<boolean>(false);

  const fetchRewardTypes: FetchRewardTypesMethod = async(quiet = false, refresh) => {
    if(!quiet) setFetched(false);
    if(refresh) clearCache("getRewardTypes");

    try {
      const response = await withCache(getRewardTypes, [params], 300_000);
      setRewardTypes(response);
      return response;
    } catch (err) {
      console.error("Error fetching reward types", err);
      throw new Error("Error fetching reward types", {cause: err});
    } finally {
      setFetched(true);
    }
  };

  useEffect(() => {
    fetchRewardTypes();
  }, [JSON.stringify(params)]);

  return {
    fetched,
    refresh: quiet =>  fetchRewardTypes(quiet, true),
    rewardTypes
  };
};
